import { FC, useMemo, useState } from "react"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"

import ButtonGroup from "../../../button-group"
import If from "../../../if"
import GraphSkeleton from "../../../skeletons/graph"
import { useFindChartDataByProfileIdQuery } from "../../../../../redux/queries"
import { usePricingHighchartsOptions } from "../../../../hooks"
import { getLineChartOptions } from "../../../chart-with-stats/options"

interface Props {
  profileId: string
  profileTitle: string
}

const PerformanceChart: FC<Props> = ({ profileId, profileTitle }) => {
  const labelDateFormat = "LLL dd yy"
  const [range, setRange] = useState<number>(365)
  const FILTER_BUTTONS = [
    {
      key: 365,
      label: "1 Year",
      disabled: false,
      onClick: () => setRange(365)
    },
    {
      key: 90,
      label: "90 Days",
      disabled: false,
      onClick: () => setRange(90)
    },
    {
      key: 30,
      label: "30 Days",
      disabled: false,
      onClick: () => setRange(30)
    }
  ]
  const { data: chartData, isFetching } = useFindChartDataByProfileIdQuery(
    { profileId: profileId ?? "", range },
    {
      skip: !profileId,
      refetchOnMountOrArgChange: true
    }
  )

  const containsData = useMemo(
    () => chartData && chartData.data.length > 0,
    [chartData]
  )
  const seriesOptions = usePricingHighchartsOptions(
    chartData?.data as string[][] | number[][],
    containsData,
    profileTitle
  )

  return (
    <div className="pt-8 pb-6 pr-4">
      <div className="pb-8 flex items-center">
        <ButtonGroup buttons={FILTER_BUTTONS} active={range} />
      </div>
      <If
        condition={isFetching}
        then={
          <GraphSkeleton
            className="pt-36"
            lineClassName="mt-16 mb-12"
            showTopTiles={false}
            showBottomTiles={true}
          />
        }
        else={
          <div className="relative">
            {!containsData && (
              <div className="w-full absolute top-1/3 text-center text-sm z-[1]">
                No data yet
              </div>
            )}
            <HighchartsReact
              highcharts={Highcharts}
              options={getLineChartOptions(
                containsData,
                seriesOptions,
                labelDateFormat
              )}
            />
          </div>
        }
      />
    </div>
  )
}

export default PerformanceChart
